'use client'

/**
 * Labeled desk value. Same scan: mono label + tabular value.
 * Empty / unknown is always — . Tone pairs with signed text in the caller.
 */

import React, { type CSSProperties, type ReactNode } from 'react'
import { DESK_EMPTY } from './DeskChassis'
import styles from './DeskReadout.module.css'

export type DeskReadoutTone = 'neutral' | 'gain' | 'loss'
export type DeskReadoutEmphasis = 'default' | 'lead' | 'quiet'

export interface DeskReadoutProps {
  label: ReactNode
  value?: ReactNode
  hint?: ReactNode
  extra?: ReactNode
  empty?: boolean
  tone?: DeskReadoutTone
  emphasis?: DeskReadoutEmphasis
  className?: string
  /** Extra class on the value element only (e.g. the grey Unavailable dash); the label keeps its own. */
  valueClassName?: string
}

function isBlank(value: ReactNode): boolean {
  if (value == null) return true
  if (typeof value === 'string' && value.trim() === '') return true
  return false
}

export function DeskReadout({
  label,
  value,
  hint,
  extra,
  empty = false,
  tone = 'neutral',
  emphasis = 'default',
  className = '',
  valueClassName,
}: DeskReadoutProps) {
  const vacant = empty || isBlank(value)
  const face = vacant ? DESK_EMPTY : value
  const toneClass = tone === 'gain' ? styles.gain : tone === 'loss' ? styles.loss : ''
  const emphasisClass =
    emphasis === 'lead' ? styles.lead : emphasis === 'quiet' ? styles.quiet : ''

  return (
    <div
      className={`${styles.readout} ${emphasisClass} ${toneClass} ${className}`.trim()}
      data-desk-readout={emphasis}
      data-tone={tone}
    >
      <span className={styles.label}>{label}</span>
      <span className={valueClassName ? `${styles.value} ${valueClassName}` : styles.value}>{face}</span>
      {hint ? <span className={styles.hint}>{hint}</span> : null}
      {extra ? <div className={styles.extra}>{extra}</div> : null}
    </div>
  )
}

export interface DeskReadoutRowProps {
  children: ReactNode
  columns?: number
  className?: string
  'aria-label'?: string
}

export function DeskReadoutRow({
  children,
  columns = 4,
  className = '',
  'aria-label': ariaLabel,
}: DeskReadoutRowProps) {
  const grid: CSSProperties = {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  }
  return (
    <div
      className={`${styles.row} ${className}`.trim()}
      style={grid}
      role="list"
      aria-label={ariaLabel}
    >
      {children}
    </div>
  )
}
